import React, { useState } from 'react'
import {
  Card,
  Button,
  Row,
  Col,
  Typography,
  Steps,
  Table,
  Tag,
  Progress,
  Space,
  Modal,
  Descriptions,
  message,
  Divider,
} from 'antd'
import {
  RobotOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
  SettingOutlined,
  AuditOutlined,
  SyncOutlined,
  FileSyncOutlined,
  MessageOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import './TextbookWriting.css'

const { Title, Paragraph, Text } = Typography

// 任务状态枚举
type TaskStatus = 'pending' | 'generating' | 'reviewing' | 'completed' | 'failed'

// 任务接口
interface WritingTask {
  id: string
  textbookName: string
  category: string
  status: TaskStatus
  progress: number
  currentStage: string
  termConsistency: number
  duplicateRate: number
  accuracy: number
  createdAt: string
  updatedAt: string
}

// 模拟任务数据
const mockTasks: WritingTask[] = [
  {
    id: '1',
    textbookName: '中医基础理论',
    category: '中医临床',
    status: 'generating',
    progress: 65,
    currentStage: '生成阶段',
    termConsistency: 92,
    duplicateRate: 5,
    accuracy: 88,
    createdAt: '2024-01-15 10:00:00',
    updatedAt: '2024-01-15 14:30:00',
  },
  {
    id: '2',
    textbookName: '方剂学',
    category: '中医临床',
    status: 'reviewing',
    progress: 85,
    currentStage: '审核阶段',
    termConsistency: 95,
    duplicateRate: 3,
    accuracy: 91,
    createdAt: '2024-01-14 09:00:00',
    updatedAt: '2024-01-15 15:00:00',
  },
  {
    id: '3',
    textbookName: '中药学',
    category: '中药',
    status: 'completed',
    progress: 100,
    currentStage: '已完成',
    termConsistency: 98,
    duplicateRate: 2,
    accuracy: 94,
    createdAt: '2024-01-10 08:00:00',
    updatedAt: '2024-01-13 18:00:00',
  },
  {
    id: '4',
    textbookName: '中医诊断学',
    category: '中医临床',
    status: 'pending',
    progress: 0,
    currentStage: '待开始',
    termConsistency: 0,
    duplicateRate: 0,
    accuracy: 0,
    createdAt: '2024-01-15 16:00:00',
    updatedAt: '2024-01-15 16:00:00',
  },
]

const TextbookWriting: React.FC = () => {
  const navigate = useNavigate()
  const [tasks, setTasks] = useState<WritingTask[]>(mockTasks)
  const [selectedTask, setSelectedTask] = useState<WritingTask | null>(null)
  const [detailModalVisible, setDetailModalVisible] = useState(false)
  const [loading, setLoading] = useState(false)

  // 获取状态标签
  const getStatusTag = (status: TaskStatus) => {
    const statusMap = {
      pending: { color: 'default', text: '待开始' },
      generating: { color: 'processing', text: '生成中' },
      reviewing: { color: 'warning', text: '审核中' },
      completed: { color: 'success', text: '已完成' },
      failed: { color: 'error', text: '失败' },
    }
    const statusInfo = statusMap[status]
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
  }

  // 启动编写 - 跳转到聊天页面
  const handleStartWriting = () => {
    navigate('/textbook/writing/chat')
  }

  // 查看任务详情
  const handleViewDetail = (task: WritingTask) => {
    setSelectedTask(task)
    setDetailModalVisible(true)
  }

  // 暂停任务
  const handlePause = (taskId: string) => {
    message.info('暂停功能开发中')
  }

  // 恢复任务
  const handleResume = (taskId: string) => {
    message.info('恢复功能开发中')
  }

  // 刷新任务
  const handleRefresh = (taskId: string) => {
    message.info('刷新功能开发中')
  }

  // 表格列定义
  const columns = [
    {
      title: '教材名称',
      dataIndex: 'textbookName',
      key: 'textbookName',
      width: 200,
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 120,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: TaskStatus) => getStatusTag(status),
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      width: 200,
      render: (progress: number, record: WritingTask) => (
        <Progress
          percent={progress}
          status={record.status === 'failed' ? 'exception' : 'active'}
          size="small"
        />
      ),
    },
    {
      title: '当前阶段',
      dataIndex: 'currentStage',
      key: 'currentStage',
      width: 120,
    },
    {
      title: '质量指标',
      key: 'quality',
      width: 300,
      render: (_: any, record: WritingTask) => (
        <Space size="small">
          <Text type="secondary" style={{ fontSize: 12 }}>
            术语一致性: <Text strong>{record.termConsistency}%</Text>
          </Text>
          <Divider type="vertical" />
          <Text type="secondary" style={{ fontSize: 12 }}>
            重复率: <Text strong>{record.duplicateRate}%</Text>
          </Text>
          <Divider type="vertical" />
          <Text type="secondary" style={{ fontSize: 12 }}>
            准确率: <Text strong>{record.accuracy}%</Text>
          </Text>
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: any, record: WritingTask) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          {record.status === 'generating' && (
            <Button
              type="link"
              size="small"
              icon={<PauseCircleOutlined />}
              onClick={() => handlePause(record.id)}
            >
              暂停
            </Button>
          )}
          {record.status === 'pending' && (
            <Button
              type="link"
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => handleResume(record.id)}
            >
              启动
            </Button>
          )}
          <Button
            type="link"
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => handleRefresh(record.id)}
          >
            刷新
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div className="textbook-writing-page">
      {/* 页面头部 */}
      <div className="writing-header">
        <div className="header-content">
          <div className="header-title-wrapper">
            <RobotOutlined className="header-icon" />
            <Title level={2} className="header-title">智能写书</Title>
          </div>
          <Paragraph className="header-subtitle">
            AI自动生成高质量的中医药教材内容，支持批量编写和质量审核
          </Paragraph>
        </div>
      </div>

      {/* 编写流程展示 */}
      <Card className="process-card" title="编写流程">
        <Steps
          current={2}
          items={[
            {
              title: '规划阶段',
              description: '生成教材大纲、审核大纲',
              icon: <FileTextOutlined />,
            },
            {
              title: '生成阶段',
              description: '生成章节内容、一致性检查、去重处理',
              icon: <RobotOutlined />,
            },
            {
              title: '优化阶段',
              description: '术语标准化、格式规范化、交叉引用',
              icon: <SyncOutlined />,
            },
            {
              title: '审核阶段',
              description: '内容完整性检查、专业准确性检查、交叉验证',
              icon: <AuditOutlined />,
            },
            {
              title: '输出阶段',
              description: '格式处理、多格式生成、版本管理',
              icon: <FileSyncOutlined />,
            },
          ]}
        />
      </Card>

      {/* 功能模块卡片 */}
      <Row gutter={[24, 24]} className="function-cards">
        <Col xs={24} sm={12} lg={6}>
          <Card
            className="function-card"
            hoverable
            onClick={handleStartWriting}
          >
            <div className="function-icon-wrapper" style={{ color: '#1890FF' }}>
              <MessageOutlined />
            </div>
            <Title level={4} className="function-title">自动编写</Title>
            <Paragraph className="function-description">
              通过对话方式描述需求，AI自动生成符合规则库标准的教材大纲和内容
            </Paragraph>
            <Button type="primary" block className="function-button">
              开始对话
            </Button>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="function-card" hoverable>
            <div className="function-icon-wrapper" style={{ color: '#52C41A' }}>
              <AuditOutlined />
            </div>
            <Title level={4} className="function-title">质量审核</Title>
            <Paragraph className="function-description">
              查看审核任务，三级审核流程确保内容质量
            </Paragraph>
            <Button block className="function-button">查看审核</Button>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="function-card" hoverable>
            <div className="function-icon-wrapper" style={{ color: '#FA8C16' }}>
              <SyncOutlined />
            </div>
            <Title level={4} className="function-title">一致性检查</Title>
            <Paragraph className="function-description">
              查看检查结果，术语一致性、去重检查
            </Paragraph>
            <Button block className="function-button">查看检查</Button>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="function-card" hoverable>
            <div className="function-icon-wrapper" style={{ color: '#722ED1' }}>
              <SettingOutlined />
            </div>
            <Title level={4} className="function-title">工作流管理</Title>
            <Paragraph className="function-description">
              管理工作流，任务调度、进度跟踪
            </Paragraph>
            <Button block className="function-button">管理工作流</Button>
          </Card>
        </Col>
      </Row>

      {/* 编写任务列表 */}
      <Card className="tasks-card" title="编写任务列表">
        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个任务`,
          }}
          loading={loading}
        />
      </Card>

      {/* 任务详情模态框 */}
      <Modal
        title="任务详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={800}
      >
        {selectedTask && (
          <Descriptions column={2} bordered>
            <Descriptions.Item label="教材名称" span={2}>
              {selectedTask.textbookName}
            </Descriptions.Item>
            <Descriptions.Item label="分类">{selectedTask.category}</Descriptions.Item>
            <Descriptions.Item label="状态">
              {getStatusTag(selectedTask.status)}
            </Descriptions.Item>
            <Descriptions.Item label="进度" span={2}>
              <Progress percent={selectedTask.progress} />
            </Descriptions.Item>
            <Descriptions.Item label="当前阶段" span={2}>
              {selectedTask.currentStage}
            </Descriptions.Item>
            <Descriptions.Item label="术语一致性">
              <Text strong>{selectedTask.termConsistency}%</Text>
            </Descriptions.Item>
            <Descriptions.Item label="重复率">
              <Text strong>{selectedTask.duplicateRate}%</Text>
            </Descriptions.Item>
            <Descriptions.Item label="准确率">
              <Text strong>{selectedTask.accuracy}%</Text>
            </Descriptions.Item>
            <Descriptions.Item label="创建时间" span={2}>
              {selectedTask.createdAt}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间" span={2}>
              {selectedTask.updatedAt}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  )
}

export default TextbookWriting

